import { useEffect, useState } from 'react';
import { api } from '../api';
import type { ConversationListEntry } from '../types';
import { useChat } from '../store';
import { ConversationListItem } from './ConversationListItem';

interface ArchivedSidebarProps {
  onOpenChat: (conversationId: string) => void;
}

export function ArchivedSidebar({ onOpenChat }: ArchivedSidebarProps) {
  const [archived, setArchived] = useState<ConversationListEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const selectedConversationId = useChat((s) => s.selectedConversationId);

  async function load() {
    setLoading(true);
    try {
      const res = await api.conversations(true);
      setArchived(res.conversations);
    } catch {
      setArchived([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <aside className="w-80 border-r border-zinc-900 flex flex-col h-full bg-zinc-950">
      <header className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between">
        <h1 className="text-base font-semibold">Archived</h1>
        <button
          onClick={load}
          className="text-[11px] text-zinc-400 hover:text-zinc-100 transition"
        >
          Refresh
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
        {loading && <p className="text-center text-xs text-zinc-500 py-8">Loading…</p>}
        {!loading && archived !== null && archived.length === 0 && (
          <div className="text-center text-xs text-zinc-500 py-12 px-4">
            <p className="font-medium text-zinc-300 mb-1">No archived chats</p>
            <p>Archive a conversation from its settings to hide it from the main list.</p>
          </div>
        )}
        {!loading && archived !== null && archived.length > 0 && (
          archived.map((c) => (
            <ConversationListItem
              key={c.id}
              conversation={c}
              selected={c.id === selectedConversationId}
              onClick={() => onOpenChat(c.id)}
            />
          ))
        )}
      </div>
    </aside>
  );
}
